/**
 * Prüfungsmodus.
 *
 * Nachgebaut wird der Bogen, nicht die Prüfung: Zahl der Fragen, Mindestpunkte je Teil
 * und Bearbeitungszeit stehen im Bewertungsschlüssel der Wasserstraßen- und
 * Schifffahrtsverwaltung (`data/sources/pruefung-sbf.json`), und der Modus liest sie von
 * dort. Die Fragen selbst kommen aus dem eigenen Bestand, nicht aus dem amtlichen
 * Fragenkatalog, und die Navigationsaufgabe der Prüfung See fehlt, weil die App keine
 * Kartenarbeit abnimmt. Beides sagt der Modus auf dem Bildschirm.
 *
 * Welche unserer Fragen zum Basisteil gehören, ist nicht erfunden: Anlage 3 der
 * Sportbootführerscheinverordnung zählt unter den Basiskenntnissen "allgemeine
 * Ausweichregeln, Schallsignale und Lichterführung" auf. Seezeichen sind dort nicht
 * genannt und stehen deshalb im spezifischen Teil.
 */
import source from '../../data/sources/pruefung-sbf.json'
import { allCardIds, binnenCardIds, questionFor, rng, type QuizQuestion } from './quiz'
import type { Progress } from './progress'
import { cardOf } from './progress'
import { dayNumber } from './srs'

export type ExamArea = 'see' | 'binnen'

export interface ExamPart {
  name: string
  questions: number
  minPoints?: number
  sharedMinPoints?: number
}

export interface ExamVariant {
  title: string
  area: ExamArea
  total: number
  minutes: number
  parts: ExamPart[]
}

export interface ExamSource {
  id: string
  title: string
  url: string
  retrieved: string
  license: string
  stand?: string
  variants: ExamVariant[]
}

export const examSource = source as ExamSource

/** Teile, die der Bestand nicht füllen kann. Kartenarbeit gehört nicht in diese App. */
const NOT_COVERED = /navigation/i

/**
 * Kartenarten, aus denen der Basisteil zieht, je Bogen.
 *
 * Auf See sind es die Lichterbilder und die Schallsignale der KVR. Im Bogen für den
 * Binnenschein stehen an der Stelle der Seesignale die Schallzeichen der Anlage 6 der
 * Ordnung: dort heißt derselbe Ton etwas anderes, ein kurzer Ton bei der Begegnung etwa
 * "Ich will an Backbord vorbeifahren" statt "Ich ändere meinen Kurs nach Steuerbord".
 * Bis Goal 20 hatte der Bestand nur Kapitel 3 der Ordnung, und der Binnenbogen borgte die
 * Seesignale; das stand hier als Hinweis und ist mit Kapitel 4 und Anlage 6 erledigt.
 */
const BASIS_KINDS = {
  see: ['state', 'signal'],
  binnen: ['state', 'binnenSignal'],
} as const

/**
 * Teile, die ausschließlich aus den Lichterbildern der Ordnung ziehen.
 *
 * Der spezifische Teil der Binnenprüfung fragt die Binnenschifffahrtsstraßen-Ordnung ab,
 * und deren Bilder unterscheiden sich von der See: ein Topplicht statt zweier, blaue
 * Lichter bei gefährlichen Gütern, eigene Bezeichnung für Verbände.
 */
const BINNEN_TEIL = /Binnen/i

export interface ExamPlan {
  variant: ExamVariant
  /** Teile, für die der Bestand Fragen hat. */
  covered: ExamPart[]
  /** Teile, die der Modus weglässt, mit Begründung auf dem Bildschirm. */
  skipped: ExamPart[]
}

/** Der Bogen zu einer Befähigung, wie ihn die Quelle beschreibt. */
export function examPlan(area: ExamArea, title?: string): ExamPlan {
  const variant = examSource.variants.find(
    (v) => v.area === area && (title ? v.title === title : v.parts.some((p) => p.name === 'Basis')),
  )
  if (!variant) throw new Error(`Für "${area}" gibt es im Bewertungsschlüssel keinen Bogen`)
  return {
    variant,
    covered: variant.parts.filter((p) => !NOT_COVERED.test(p.name)),
    skipped: variant.parts.filter((p) => NOT_COVERED.test(p.name)),
  }
}

export interface ExamQuestion {
  part: string
  cardId: string
  question: QuizQuestion
}

export interface Exam {
  plan: ExamPlan
  questions: ExamQuestion[]
  /** Beginn in Millisekunden, für die Uhr. */
  startedAt: number
  seed: number
}

/**
 * Zieht die Fragen. Schwachstellen zuerst: was schlechter beantwortet wurde, kommt eher
 * dran, damit ein Durchgang zeigt, was fehlt, statt zu schmeicheln.
 */
export function buildExam(
  plan: ExamPlan,
  progress: Progress,
  seed: number,
  startedAt: number,
  today = dayNumber(),
): Exam {
  const random = rng(seed)
  // Erst mischen, dann nach Schwäche sortieren: ohne Lernstand sind alle Karten gleich
  // schwach, und ohne Mischen käme jedes Mal derselbe Anfang des Bestands.
  const pool = allCardIds()
  shuffleInPlace(pool, random)
  const weakFirst = pool.sort((a, b) => weakness(progress, b, today) - weakness(progress, a, today))
  const binnenPool = binnenCardIds().filter((id) => id.startsWith('binnen:'))
  shuffleInPlace(binnenPool, random)
  // Im Binnenbogen gehören die Schallzeichen der Ordnung in den Basisteil, an die Stelle
  // der Seesignale; der Pool des Basisteils nimmt sie deshalb dazu.
  const binnenBogen = plan.variant.area === 'binnen'
  // Die Seesignale verlassen den Pool des Binnenbogens ganz, nicht nur den Filter des
  // Basisteils: sonst holte sie der Rückfall zurück, sobald ein Teil nicht voll wird.
  const basisPool = binnenBogen
    ? [
        ...weakFirst.filter((id) => !id.startsWith('signal:')),
        ...binnenCardIds().filter((id) => id.startsWith('binnenSignal:')),
      ]
    : weakFirst
  shuffleInPlace(basisPool, random)
  basisPool.sort((a, b) => weakness(progress, b, today) - weakness(progress, a, today))
  const art = binnenBogen ? 'binnen' : 'see'
  binnenPool.sort((a, b) => weakness(progress, b, today) - weakness(progress, a, today))
  const used = new Set<string>()
  const questions: ExamQuestion[] = []
  for (const part of plan.covered) {
    const binnenTeil = plan.variant.area === 'binnen' && BINNEN_TEIL.test(part.name)
    const basis = part.name === 'Basis'
    const wanted = binnenTeil
      ? binnenPool.filter((id) => !used.has(id))
      : basisPool.filter((id) => !used.has(id) && basis === isBasis(id, art))
    // Der Rückfall nimmt, was übrig ist, wie vorher auch: reichen die Seezeichen für den
    // spezifischen Seeteil nicht, füllen Lichterbilder und Signale auf.
    const fallback = binnenTeil ? wanted : basisPool.filter((id) => !used.has(id))
    const source = wanted.length >= part.questions ? wanted : fallback
    for (let i = 0; i < part.questions; i += 1) {
      const cardId = source[i]
      if (cardId === undefined) throw new Error(`Der Bestand reicht für "${part.name}" nicht`)
      used.add(cardId)
      questions.push({ part: part.name, cardId, question: questionFor(cardId, rng(seed + questions.length)) })
    }
  }
  // Die Reihenfolge der Teile bleibt, innerhalb eines Teils wird gemischt.
  for (const part of plan.covered) {
    const inPart = questions.filter((q) => q.part === part.name)
    shuffleInPlace(inPart, random)
    let at = 0
    for (let i = 0; i < questions.length; i += 1) {
      if (questions[i]!.part === part.name) questions[i] = inPart[at++]!
    }
  }
  return { plan, questions, startedAt, seed }
}

function isBasis(cardId: string, art: 'see' | 'binnen' = 'see'): boolean {
  const kind = cardId.slice(0, cardId.indexOf(':'))
  return (BASIS_KINDS[art] as readonly string[]).includes(kind)
}

/** Je schlechter eine Karte sitzt, desto größer die Zahl. Unbekannte Karten liegen dazwischen. */
function weakness(progress: Progress, cardId: string, today: number): number {
  const card = progress.cards[cardId]
  if (!card) return 0.5
  if (card.attempts === 0) return 0.5
  const rate = card.correct / card.attempts
  const overdue = Math.max(0, today - card.dueDay) / 30
  return 1 - rate + Math.min(0.3, overdue)
}

function shuffleInPlace<T>(items: T[], random: () => number): void {
  for (let i = items.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1))
    ;[items[i], items[j]] = [items[j]!, items[i]!]
  }
}

/** Verbleibende Zeit in Sekunden. */
export function remainingS(exam: Exam, now: number): number {
  const total = exam.plan.variant.minutes * 60
  return Math.max(0, total - Math.floor((now - exam.startedAt) / 1000))
}

export function expired(exam: Exam, now: number): boolean {
  return remainingS(exam, now) === 0
}

/**
 * Eine Antwort setzen. Nach Ablauf der Zeit nimmt der Bogen nichts mehr an: in der
 * Prüfung wird eingesammelt, und was dann nicht dasteht, zählt nicht.
 */
export function answerAt(
  exam: Exam,
  answers: Record<number, number>,
  index: number,
  option: number,
  now: number,
): Record<number, number> {
  if (expired(exam, now)) return answers
  if (index < 0 || index >= exam.questions.length) return answers
  const question = exam.questions[index]!.question
  if (option < 0 || option >= question.options.length) return answers
  return { ...answers, [index]: option }
}

export interface PartResult {
  name: string
  questions: number
  points: number
  /** Mindestpunkte dieses Teils, falls die Quelle sie einzeln nennt. */
  minPoints?: number
  passed: boolean
}

export interface ExamResult {
  parts: PartResult[]
  points: number
  questions: number
  passed: boolean
  /** Teile, die der Modus nicht stellt, wörtlich aus der Quelle. */
  skipped: string[]
}

/**
 * Wertet nach dem Schlüssel aus: jeder Teil für sich. Wo die Quelle die Mindestpunkte
 * über mehrere Teile zusammenfasst ("insgesamt mind. 20 Punkte"), zählt die Summe dieser
 * Teile. Ein durchgefallener Teil lässt den ganzen Bogen durchfallen.
 */
export function evaluate(exam: Exam, answers: Record<number, number>): ExamResult {
  const points: Record<string, number> = {}
  for (const part of exam.plan.covered) points[part.name] = 0
  exam.questions.forEach((q, index) => {
    if (answers[index] === q.question.correct) points[q.part] = (points[q.part] ?? 0) + 1
  })
  const sharedTotal = exam.plan.covered
    .filter((p) => p.sharedMinPoints !== undefined)
    .reduce((n, p) => n + (points[p.name] ?? 0), 0)
  const parts: PartResult[] = exam.plan.covered.map((part) => {
    const got = points[part.name] ?? 0
    const passed =
      part.minPoints !== undefined
        ? got >= part.minPoints
        : part.sharedMinPoints !== undefined
          ? sharedTotal >= part.sharedMinPoints
          : true
    return {
      name: part.name,
      questions: part.questions,
      points: got,
      ...(part.minPoints !== undefined ? { minPoints: part.minPoints } : {}),
      passed,
    }
  })
  return {
    parts,
    points: parts.reduce((n, p) => n + p.points, 0),
    questions: parts.reduce((n, p) => n + p.questions, 0),
    passed: parts.every((p) => p.passed),
    skipped: exam.plan.skipped.map((p) => p.name),
  }
}

/** Nach einem Durchgang: der Lernstand bekommt jede Antwort mit. */
export function gradeOf(correct: boolean): 5 | 2 {
  return correct ? 5 : 2
}

export { cardOf }
